import React, { FC, ChangeEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import {
  GssSettingCharacterOrderData,
  GssCharacterOrder,
  Student
} from 'types/schema-generated-types';
import GET_GSS_STUDENT_SETTINGS from 'graph/settings/gss/get-gss-student-settings-query';
import UPDATE_GSS_STUDENT_CHARACTER_ORDER_SETTING from 'graph/settings/gss/update-gss-student-character-order-setting-mutation';
import REMOVE_STUDENT_SETTING from 'graph/settings/common/remove-student-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface LanguageOptionsInputs {
  label: string;
  value: string;
}

interface CharacterOrderProps {
  student: Student;
  setting: GssSettingCharacterOrderData | null;
  classroomSetting: GssSettingCharacterOrderData | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
  panel: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    }
  };
});

const CLASSROOM_LEVEL_SETTING_PLAINTEXT_MAP = {
  [GssCharacterOrder.gss]: 'GSS Order',
  [GssCharacterOrder.natural]: 'Alphabetical/Numerical Order',
  [GssCharacterOrder.freePlay]: 'Free Play'
};

const getClassroomLevelSettingPlaintext = (value: GssCharacterOrder) => {
  return value
    ? CLASSROOM_LEVEL_SETTING_PLAINTEXT_MAP[value]
    : CLASSROOM_LEVEL_SETTING_PLAINTEXT_MAP[GssCharacterOrder.gss];
};

const letterOptions: LanguageOptionsInputs[] = [
  { label: `Use Class Level Setting `, value: '' },
  {
    label: 'GSS Order (Letters in GSS order and numbers in numerical order)',
    value: GssCharacterOrder.gss
  },
  {
    label:
      'Alphabetical/Numerical Order (Letters in alphabetical order and numbers in numerical order)',
    value: GssCharacterOrder.natural
  },
  { label: 'Free Play (All letters and numbers unlocked)', value: GssCharacterOrder.freePlay }
];

const getStudentGssSettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_GSS_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (
        cachedData,
        { updateGssStudentSettingCharacterOrder }
      ): { gssStudentSettings: any } => {
        const [{ value: characterOrder }] = updateGssStudentSettingCharacterOrder;
        return {
          gssStudentSettings: {
            ...cachedData?.gssStudentSettings,
            characterOrder
          }
        };
      }
    }
  ])
});

const removeStudentSettingMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_GSS_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (cachedData): { gssStudentSettings: any } => {
        return {
          gssStudentSettings: {
            ...cachedData?.gssStudentSettings,
            characterOrder: null
          }
        };
      }
    }
  ])
});

const CharacterOrder: FC<CharacterOrderProps> = ({
  student,
  setting,
  classroomSetting,
  expanded,
  handleChangeExpanded,
  panel
}) => {
  const { t } = useTranslation('CharacterOrderSetting');
  const classes = useStyles();

  const studentId = +student.id;
  const [, { openAlert }] = useStore();
  const [value, setValue] = useState(setting?.letters || '');
  const [classroomSettingValue] = useState(classroomSetting?.letters || '');

  const [updateGssStudentSettingCharacterOrder] = useMutation(
    UPDATE_GSS_STUDENT_CHARACTER_ORDER_SETTING,
    getStudentGssSettingsMutationCacheUpdate(studentId)
  );
  const [removeStudentSetting] = useMutation(
    REMOVE_STUDENT_SETTING,
    removeStudentSettingMutationCacheUpdate(studentId)
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue((event.target as HTMLInputElement).value);
  };

  const handleOnCancel = (): void => setValue(setting?.letters || '');

  const handleOnConfirm = async (): Promise<void> => {
    try {
      if (value === '') {
        await removeStudentSetting({
          variables: {
            where: {
              studentId,
              setting: 'characterOrder',
              appName: 'gss'
            }
          }
        });
      } else {
        await updateGssStudentSettingCharacterOrder({
          variables: {
            data: {
              studentId,
              value: {
                letters: value,
                numbers: value,
                wdtCapitals: value,
                wdtLowercase: value,
                wdtNumbers: value
              }
            }
          }
        });
      }
      openAlert(getSettingSuccessAlert('Letter and Number Order', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  const updatedLetterOptions = letterOptions.map(option => {
    let label = option.label;
    if (!option.value) {
      const classroomSelectedLabel = getClassroomLevelSettingPlaintext(
        classroomSettingValue as GssCharacterOrder
      );
      label = `${option.label} - ${classroomSelectedLabel}`;
    }

    return { ...option, label };
  });

  return (
    <SettingsCard
      title={t('Letter and Number Order')}
      description={t(
        'Choose the letter and number order students will use (Default is GSS order).'
      )}
      expanded={expanded}
      panel={panel}
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <RadioGroup
          aria-label="letter number setting"
          name="letterNumberSetting"
          value={value}
          onChange={handleChange}
        >
          {updatedLetterOptions.map(
            ({ label, value }, key): ReactElement => (
              <FormControlLabel
                key={`letter-number-option-${key}`}
                value={value}
                control={<Radio />}
                label={label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default CharacterOrder;
